import { useParams, Link, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { getProductById } from '@/lib/queries/products'
import { getListingsByProductId } from '@/lib/queries/listings'
import type { ProductDetail } from '@/lib/queries/products'
import type { ListingListItem } from '@/lib/queries/listings'
import { productService } from '@/services/productService'
import { IoPencilSharp } from 'react-icons/io5'
import { useState } from 'react'
import toast from 'react-hot-toast'

/**
 * Product Detail page
 * Mobile-first design optimized for iPad Mini
 * Based on wireframe: product-detail.html
 */
export default function ProductDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [showMenu, setShowMenu] = useState(false)

  const { mutateAsync: doDelete, isPending: isDeleting } = useMutation({
    mutationFn: (productId: string) => productService.deleteProduct(productId),
    onSuccess: async () => {
      toast.success('Product deleted successfully')
      await queryClient.invalidateQueries({ queryKey: ['products'] })
      await queryClient.invalidateQueries({ queryKey: ['listingsByProduct'] })
      await queryClient.refetchQueries({ queryKey: ['products'] })
      navigate('/products')
    },
  })

  const handleDelete = async () => {
    if (!id) return
    const ok = window.confirm('Are you sure you want to delete this product? This cannot be undone.')
    if (!ok) return
    setShowMenu(false)
    try {
      await doDelete(id)
    } catch (error) {
      toast.error(`Failed to delete product: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  const { data: product, isLoading, error } = useQuery({
    queryKey: ['product', id],
    queryFn: () => getProductById(id!),
    enabled: !!id,
    staleTime: 0,
    gcTime: 0,
  })

  // Fetch listings for this product
  const { data: listings = [], isLoading: listingsLoading } = useQuery({
    queryKey: ['listingsByProduct', id],
    queryFn: () => getListingsByProductId(id!),
    enabled: !!id && !!product?._id,
    staleTime: 0,
    gcTime: 0,
  })

  // Helper to format price (GBP)
  const formatPrice = (price: number | undefined): string => {
    if (price === undefined || price === null) return 'N/A'
    return `£${price.toFixed(2)}`
  }

  // Helper to format date (UK format: DD MM YYYY)
  const formatDate = (dateString: string | undefined): string => {
    if (!dateString) return 'N/A'
    try {
      const date = new Date(dateString)
      const day = String(date.getDate()).padStart(2, '0')
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const year = date.getFullYear()
      return `${day} ${month} ${year}`
    } catch {
      return 'N/A'
    }
  }

  // Helper to get product image URL
  const getProductImageUrl = (product: ProductDetail): string | null => {
    // Try hero image first
    if (product.media?.hero?.url) {
      return product.media.hero.url
    }
    // Try first gallery image
    if (product.media?.galleryImages && product.media.galleryImages.length > 0) {
      return product.media.galleryImages[0].url || null
    }
    return null
  }

  // Helper to get channel display name
  const getChannelDisplayName = (channelCode: string | null | undefined): string => {
    if (!channelCode) return 'Unknown'
    return channelCode.charAt(0).toUpperCase() + channelCode.slice(1)
  }

  // Helper to render description (portable text or string)
  const renderDescription = (description: any): string => {
    if (!description) return 'No description available'
    if (typeof description === 'string') return description
    if (Array.isArray(description)) {
      // Portable text array - extract text content
      return description
        .map((block: any) => {
          if (block._type === 'block' && block.children) {
            return block.children.map((child: any) => child.text || '').join('')
          }
          return ''
        })
        .join('\n')
    }
    return 'No description available'
  }

  // Helper to get active listings count
  const getActiveListingsCount = (listings: ListingListItem[]): number => {
    return listings.filter((listing) => listing.status === 'synced').length
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 pb-24 flex items-center justify-center">
        <div className="text-gray-500">Loading product...</div>
      </div>
    )
  }

  if (error || !product) {
    return (
      <div className="min-h-screen bg-gray-50 pb-24 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500 mb-4">Product not found</p>
          <Link
            to="/products"
            className="inline-block bg-blue-600 text-white px-6 py-3 rounded-lg font-medium touch-target-min-44"
          >
            Back to List
          </Link>
        </div>
      </div>
    )
  }

  const imageUrl = getProductImageUrl(product)
  const channel = product.channelMapping?.[0]?.channel
  const etsyConfigName =
    product.channelMapping?.find((m) => m.channel === 'etsy')?.configSet?.name || null
  const bundle = product.relations?.relatedBundle
  const description = renderDescription(product.description)
  const activeListingsCount = getActiveListingsCount(listings)
  const hasListings = listings.length > 0

  return (
    <div className="min-h-screen bg-gray-50 pb-40">
      {/* Header */}
      <header className="sticky top-0 bg-white border-b border-gray-200 z-10">
        <div className="p-4 flex justify-between items-center">
          <h1 className="text-xl font-bold text-gray-900">Product Detail</h1>
          <div className="flex gap-3">
            <Link
              to={`/products/${id}/edit`}
              className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center touch-target-min-44 hover:bg-gray-200 transition-colors"
              aria-label="Edit"
            >
              <IoPencilSharp className="w-6 h-6 text-gray-800" />
            </Link>
            <div className="relative">
              <button
                className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center touch-target-min-44 hover:bg-gray-200 transition-colors"
                aria-label="More options"
                onClick={() => setShowMenu(!showMenu)}
              >
                ⋯
              </button>
              {showMenu && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setShowMenu(false)}
                  />
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-20">
                    {!hasListings ? (
                      <button
                        className="w-full text-left px-4 py-3 text-sm text-red-600 hover:bg-red-50 transition-colors touch-target-min-44"
                        onClick={handleDelete}
                        disabled={isDeleting || listingsLoading}
                      >
                        {isDeleting ? 'Deleting...' : 'Delete Product'}
                      </button>
                    ) : (
                      <div className="px-4 py-3 text-sm text-gray-500">
                        Cannot delete: product has listings
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="p-4">
        {/* Product Image */}
        <div className="w-full aspect-square bg-gray-100 rounded-lg mb-4 flex items-center justify-center overflow-hidden">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={product.title || 'Product'}
              className="w-full h-full object-cover"
            />
          ) : (
            <span className="text-6xl">🖼️</span>
          )}
        </div>

        {/* Product Info Card */}
        <div className="bg-white rounded-lg p-4 mb-4 shadow-sm border border-gray-200">
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">
            {product.title || 'Untitled Product'}
          </h2>
          <div className="flex gap-2 flex-wrap mb-4">
            {channel && (
              <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded text-xs font-medium">
                {getChannelDisplayName(channel)}
              </span>
            )}
            {product.isPrintOnDemand ? (
              <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded text-xs font-medium">
                POD
              </span>
            ) : (
              <span className="px-3 py-1 bg-green-100 text-green-800 rounded text-xs font-medium">
                Physical
              </span>
            )}
          </div>
          {etsyConfigName && (
            <div className="mb-4">
              <div className="text-xs text-gray-600 mb-1">Etsy Config Set</div>
              <div className="text-sm text-gray-900">{etsyConfigName}</div>
            </div>
          )}
          <div className="text-3xl font-bold text-blue-600 mb-6">
            {formatPrice(product.rrp)}
          </div>

          {/* SKU */}
          <div className="mb-4">
            <div className="text-xs text-gray-600 mb-1">SKU</div>
            <div className="text-base text-gray-900">{product.sku || 'No SKU'}</div>
          </div>

          {/* Description */}
          <div className="mb-4">
            <div className="text-xs text-gray-600 mb-1">Description</div>
            <div className="text-base text-gray-900 whitespace-pre-wrap">{description}</div>
          </div>
        </div>

        {/* Bundle Information Card */}
        {bundle && (
          <div className="bg-white rounded-lg p-4 mb-4 shadow-sm border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Bundle Information</h3>
            <div className="mb-2">
              <div className="text-xs text-gray-600 mb-1">Bundle</div>
              <Link
                to={`/bundles/${bundle._id}`}
                className="text-base text-blue-600 hover:text-blue-700 font-medium"
              >
                {bundle.title || 'Untitled Bundle'} ({bundle.sku || 'No SKU'})
              </Link>
            </div>
          </div>
        )}

        {/* Listings Card */}
        <div className="bg-white rounded-lg p-4 mb-4 shadow-sm border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Listings</h3>
          {listings.length === 0 ? (
            <div className="text-sm text-gray-500 mb-4">No listings for this product</div>
          ) : (
            <>
              <div className="text-sm text-gray-600 mb-3">
                {activeListingsCount} {activeListingsCount === 1 ? 'active listing' : 'active listings'}{' '}
                for this product
              </div>
              <div className="space-y-2 mb-4">
                {listings.slice(0, 3).map((listing) => {
                  const listingPrice = listing.price
                  const basePrice = listing.relatedProduct?.rrp
                  // Only consider overridden if both prices exist and differ
                  const hasOverride = basePrice !== undefined && basePrice !== null && 
                                      listingPrice !== undefined && listingPrice !== null && 
                                      listingPrice !== basePrice
                  const channelName = getChannelDisplayName(listing.channel)
                  const statusDisplay = listing.status === 'synced' ? 'Active' : listing.status === 'not_synced' ? 'Draft' : listing.status
                  const createdDate = formatDate(listing._createdAt)

                  return (
                    <Link
                      key={listing._id}
                      to={`/listings/${listing._id}`}
                      className="block bg-gray-50 rounded-md p-3 border border-gray-200 hover:bg-gray-100 hover:border-gray-300 transition-colors touch-target-min-44"
                    >
                      <div className="font-medium text-gray-900 mb-2">
                        {listing.title || `${channelName} Listing`}
                      </div>
                      <div className="space-y-1">
                        <div className="text-sm text-gray-700">
                          <span className="font-medium">Price:</span> {formatPrice(listingPrice)}
                          {hasOverride && <span className="text-yellow-600 ml-1">(overridden)</span>}
                        </div>
                        <div className="text-sm text-gray-700">
                          <span className="font-medium">Created:</span> {createdDate}
                        </div>
                        <div className="text-xs text-gray-600 mt-1">
                          Status:{' '}
                          <span
                            className={
                              listing.status === 'synced'
                                ? 'text-green-600 font-medium'
                                : listing.status === 'not_synced'
                                  ? 'text-yellow-600 font-medium'
                                  : 'text-gray-600'
                            }
                          >
                            {statusDisplay}
                          </span>
                        </div>
                      </div>
                    </Link>
                  )
                })}
              </div>
            </>
          )}
          <Link
            to={`/listings/new?productId=${id}&step=3`}
            className="block w-full px-4 py-2 border border-gray-300 rounded-md text-sm text-center bg-white hover:bg-gray-50 transition-colors touch-target-min-44"
          >
            Create Listing
          </Link>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 mt-6">
          <Link
            to="/products"
            className="flex-1 bg-gray-100 text-gray-900 py-3 px-6 rounded-lg font-medium text-center touch-target-min-44 hover:bg-gray-200 transition-colors"
          >
            Back to List
          </Link>
        </div>
      </main>
    </div>
  )
}
